/* eslint-disable no-nested-ternary */
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useEffectOnceWhen } from "rooks";
import { CachePolicies } from "use-http";

import { JANZU_URL } from "config/config";
import { useFetchCustom } from "hooks/useFetchCustom";
import { useValueDebounce } from "hooks/useValueDebounce";
import { Typography } from "new-components/typography/Typography";
import { ListResponse } from "types/api";
import { DetectorModes } from "types/createDetector";
import { Detector } from "types/detectors";

const DETECTOR_MODE_LABELS: Record<DetectorModes | string, string> = {
  [DetectorModes.BINARY]: "(Binary)",
  [DetectorModes.COUNT]: "(Count)",
  [DetectorModes.MULTICHOICE]: "(Multiple Choice - Choose One)",
  [DetectorModes.TEXT_RECOGNITION]: "(Text Recognition)",
};

export interface SelectDetectorProps {
  actualRows?: number;
  detectorId?: string;
  detectorName?: string;
  loadingDetectorData: boolean;
  onExploreDetectorSelect?: (detector: Detector) => void;
  onFilterChange?: (value: {
    detector_id: string;
    detector_name: string;
    detector_mode: DetectorModes;
    detector_query: string;
    filterModel: string;
  }) => void;
  detectorQuery?: string;
  rowCount?: number;

  /**
   * @default false
   */
  showPulseAnimation?: boolean;
}

export const SelectDetector = React.memo((props: SelectDetectorProps) => {
  const {
    actualRows,
    detectorId,
    detectorName,
    loadingDetectorData,
    onExploreDetectorSelect,
    onFilterChange,
    rowCount,
    detectorQuery,
    showPulseAnimation = false,
  } = props;

  const [dataListDetectors, setDataListDetectors] = useState<Detector[]>();
  const [textValue, setTextValue] = useState<string>("");
  const debouncedValue = useValueDebounce<string>(textValue, 500);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const DEFAULT_PAGE_SIZE = 25;
  const detectorsUrl = `${JANZU_URL}reef-api/detectors?page_size=${DEFAULT_PAGE_SIZE}&search=${textValue}`;

  const { data: fetchedDetectors, loading: loadingDetectors } = useFetchCustom<ListResponse<Detector>>(
    detectorsUrl,
    {
      cachePolicy: CachePolicies.NO_CACHE,
    },
    [debouncedValue]
  );

  const detailDetectorLink = useMemo(() => (detectorId ? `/reef/detectors/${detectorId}` : ""), [detectorId]);
  const isDataPage = Boolean(!onExploreDetectorSelect);

  const filterFetchedDetectors = useMemo(() => fetchedDetectors?.results || [], [fetchedDetectors]);

  useEffectOnceWhen(() => {
    if (onExploreDetectorSelect && filterFetchedDetectors.length > 0 && !detectorId) {
      onExploreDetectorSelect(filterFetchedDetectors[0]);
    }
  }, Boolean(filterFetchedDetectors.length > 0 && onExploreDetectorSelect));

  useEffect(() => {
    const detectors = filterFetchedDetectors;
    if (detectors && detectors.length > 0) {
      if (detectorId && detectorName) {
        const findObject = detectors.find((item) => item.id === detectorId) as Detector;
        if (!findObject) {
          const isFilter = detectorName.includes(textValue);
          if (isFilter) {
            const object = { name: detectorName, id: detectorId, label_prompt: detectorQuery } as unknown as Detector;
            setDataListDetectors([object, ...detectors]);
            return;
          }
        }
      }
      setDataListDetectors(detectors);
    }
  }, [
    textValue,
    setDataListDetectors,
    detectorId,
    detectorName,
    detectorQuery,
    onExploreDetectorSelect,
    filterFetchedDetectors,
  ]);

  const handleClickOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickSelect = (detector: Detector) => {
    if (onExploreDetectorSelect) {
      onExploreDetectorSelect(detector);
    }
    if (onFilterChange) {
      onFilterChange({
        detector_id: detector.id,
        detector_name: detector.name,
        detector_query: detector.label_prompt,
        detector_mode: detector.mode,
        filterModel: JSON.stringify({
          items: [],
        }),
      });
    }

    handleClose();
  };

  function renderContent() {
    // Show a spinner while loading
    if (loadingDetectorData) {
      return <CircularProgress color="inherit" sx={{ width: "25px!important", height: "25px!important" }} />;
    }

    // Otherwise show the detector name
    return detectorName || "Choose Detector";
  }

  const existText = textValue.trim().length > 0;

  function getDetectorMode(mode: DetectorModes | string | undefined) {
    return mode ? (DETECTOR_MODE_LABELS[mode] ? DETECTOR_MODE_LABELS[mode] : "-") : "-";
  }

  return (
    <Box sx={{ width: '100%' }} className={`${isDataPage ? "flex px-4" : ""}`}>
      <Box sx={{ width: '100%' }} className={`${isDataPage ? "flex align-center" : ""}`}>
        {detectorId !== undefined && Boolean(!onExploreDetectorSelect) && (
          <Typography
            variant="subtitle"
            textTransform="uppercase"
            fontWeight={isDataPage ? "normal" : "semibold"}
            className={`text-neutral-700 self-center ${isDataPage ? "text-white" : ""}`}
          >
            {isDataPage ? "Detector:" : "Select a detector"}
          </Typography>
        )}
        <Button
          aria-label="more"
          id="long-button"
          aria-controls={open ? "long-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-haspopup="true"
          onClick={handleClickOpen}
          endIcon={<KeyboardArrowDownIcon width={20} height={20} />}
          className="text-white"
          sx={{
            overflow: 'hidden',
            ...(showPulseAnimation && {
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                animation: `pulseAnimation 2s infinite`,
                "@keyframes pulseAnimation": {
                  "0%": {
                    boxShadow: "0 0 0 0 rgba(0, 0, 0, 0.4)",
                  },
                  "100%": {
                    boxShadow: "0 0 0 20px rgba(0, 0, 0, 0)",
                  },
                },

                borderRadius: "6px",
                zIndex: 1_000_000,
              },
            }),
          }}
        >
          <Typography
            variant="subtitle"
            textTransform="uppercase"
            fontWeight="medium"
            noWrap
          >
            {renderContent()}
          </Typography>
        </Button>
      </Box>
      <Menu
        id="long-menu"
        MenuListProps={{
          "aria-labelledby": "long-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            padding: "10px",
            width: "928px",
          },
        }}
      >
        <TextField
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
          size="small"
          fullWidth
          placeholder="Search by Name/ID..."
          className="mb-2"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Box className="flex gap-2">
                  {existText && (
                    <IconButton className="p-1" onClick={() => setTextValue("")}>
                      <CloseIcon sx={{ opacity: "50%" }} />
                    </IconButton>
                  )}
                  <IconButton className="p-1">
                    <SearchIcon sx={{ transform: "rotate(90deg)", opacity: "50%" }} />
                  </IconButton>
                </Box>
              </InputAdornment>
            ),
          }}
        />
        {loadingDetectors ? (
          <Box>
            <Skeleton width="100%" height="36px" />
            <Skeleton width="100%" height="36px" />
            <Skeleton width="100%" height="36px" />
          </Box>
        ) : (
          <Box className="max-h-[180px] overflow-y-auto">
            {dataListDetectors && dataListDetectors.length > 0 ? (
              dataListDetectors.map((detector) => (
                <MenuItem
                  key={detector.id}
                  selected={detector.id === detectorId}
                  onClick={() => handleClickSelect(detector)}
                >
                  <Box className="flex gap-2">
                    <b>{detector.name}</b>
                    {isDataPage && <b>-</b>}
                    <Box>{onExploreDetectorSelect ? getDetectorMode(detector.mode) : detector.id}</Box>
                  </Box>
                </MenuItem>
              ))
            ) : (
                  <Typography fontWeight="bold" align="center">
                    No detectors
                  </Typography>
            )}
          </Box>
        )}
      </Menu>
      {detectorId && isDataPage && (
        <Box
          style={{ textOverflow: "ellipsis" }}
          className="self-center hidden md:flex md:overflow-hidden md:whitespace-nowrap w-full"
        >
          <Typography variant="subtitle">
            <Link className="hover:underline" to={detailDetectorLink}>
              DETECTOR ID: {detectorId}
            </Link>
            {` - SHOWING ${actualRows} / ${rowCount} QUERIES`}
          </Typography>
        </Box>
      )}
    </Box>
  );
});
